(() => {
  const rootSelector = '[data-acadian-recipes-hub]';
  const instances = new WeakMap();

  const normalise = (value) => String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[\u2018\u2019\u02bc]/g, "'")
    .replace(/[-\u2010-\u2015]/g, ' ')
    .toLowerCase()
    .trim();

  const initialise = (root) => {
    if (root.dataset.hubInitialized === 'true') return;
    const cards = Array.from(root.querySelectorAll('[data-recipe-card]'))
      .sort((a, b) => Number(a.dataset.order) - Number(b.dataset.order));
    const featuredSection = root.querySelector('[data-featured-section]');
    const featuredGrid = root.querySelector('[data-featured-grid]');
    const resultsGrid = root.querySelector('[data-results-grid]');
    const form = root.querySelector('[data-search-form]');
    const input = root.querySelector('[data-search-input]');
    const filters = Array.from(root.querySelectorAll('[data-filter]'));
    const resetButtons = Array.from(root.querySelectorAll('[data-reset]'));
    const primaryReset = root.querySelector('.acadian-recipes-hub__filters [data-reset]');
    const count = root.querySelector('[data-result-count]');
    const label = root.querySelector('[data-result-label]');
    const live = root.querySelector('[data-live-status]');
    const empty = root.querySelector('[data-empty-state]');

    if (!cards.length || !featuredSection || !featuredGrid || !resultsGrid || !form || !input) return;

    const events = new AbortController();
    const listen = (element, type, callback) => element.addEventListener(type, callback, { signal: events.signal });
    const searchIndex = new Map(cards.map((card) => [card, normalise(card.dataset.search)]));
    let selectedCategory = 'all';
    let inputTimer;
    let composing = false;
    const categoryLabels = new Map(filters.map((button) => [button.dataset.filter, button.textContent.trim()]));

    const restoreLayout = () => {
      cards.forEach((card) => {
        card.hidden = false;
        if (card.dataset.featured === 'true') featuredGrid.append(card);
        else resultsGrid.append(card);
      });
      featuredSection.hidden = false;
    };

    const mergeLayout = () => {
      cards.forEach((card) => resultsGrid.append(card));
      featuredSection.hidden = true;
    };

    const apply = ({ announce = true } = {}) => {
      const query = normalise(input.value);
      const terms = query.split(/\s+/).filter(Boolean);
      const active = Boolean(query) || selectedCategory !== 'all';
      if (active) mergeLayout();
      else restoreLayout();

      let visible = 0;
      cards.forEach((card) => {
        const searchMatch = terms.every((term) => searchIndex.get(card).includes(term));
        const categories = String(card.dataset.categories || '').split(/\s+/);
        const categoryMatch = selectedCategory === 'all' || categories.includes(selectedCategory);
        const match = searchMatch && categoryMatch;
        card.hidden = !match;
        if (match) visible += 1;
      });

      filters.forEach((button) => {
        const pressed = button.dataset.filter === selectedCategory;
        button.classList.toggle('is-active', pressed);
        button.setAttribute('aria-pressed', String(pressed));
      });

      count.textContent = String(visible);
      label.textContent = categoryLabels.get(selectedCategory) || 'All categories';
      empty.hidden = visible !== 0;
      if (primaryReset) primaryReset.hidden = !active;

      if (announce) {
        const recipeWord = visible === 1 ? 'recipe' : 'recipes';
        const message = `${query ? 'Search results. ' : ''}${visible} ${recipeWord} shown. ${categoryLabels.get(selectedCategory) || 'All recipes'}.`;
        if (live.textContent !== message) live.textContent = message;
      }
    };

    filters.forEach((button) => {
      listen(button, 'click', () => {
        window.clearTimeout(inputTimer);
        selectedCategory = button.dataset.filter;
        apply();
      });
    });

    listen(form, 'submit', (event) => {
      event.preventDefault();
      window.clearTimeout(inputTimer);
      apply();
    });

    const scheduleSearch = () => {
      window.clearTimeout(inputTimer);
      if (!composing) inputTimer = window.setTimeout(() => apply(), 240);
    };
    listen(input, 'input', scheduleSearch);
    listen(input, 'compositionstart', () => { composing = true; window.clearTimeout(inputTimer); });
    listen(input, 'compositionend', () => { composing = false; scheduleSearch(); });

    listen(input, 'search', () => { window.clearTimeout(inputTimer); apply(); });

    const reset = ({ focus = true, announce = true } = {}) => {
      window.clearTimeout(inputTimer);
      input.value = '';
      selectedCategory = 'all';
      apply({ announce });
      if (focus) input.focus();
    };
    resetButtons.forEach((button) => listen(button, 'click', () => reset()));

    root.dataset.hubInitialized = 'true';
    apply({ announce: false });
    root.querySelectorAll('[data-enhancement] input, [data-enhancement] button').forEach((control) => { control.disabled = false; });
    root.classList.add('is-enhanced');
    instances.set(root, {
      reset,
      destroy: () => {
        window.clearTimeout(inputTimer);
        events.abort();
        instances.delete(root);
        delete root.dataset.hubInitialized;
      },
    });
  };

  const rootsWithin = (target) => [
    ...(target.matches?.(rootSelector) ? [target] : []),
    ...target.querySelectorAll(rootSelector),
  ];
  rootsWithin(document).forEach(initialise);
  document.addEventListener('shopify:section:load', (event) => rootsWithin(event.target).forEach(initialise));
  document.addEventListener('shopify:section:unload', (event) => {
    rootsWithin(event.target).forEach((root) => instances.get(root)?.destroy());
  });
  document.addEventListener('shopify:block:select', (event) => {
    const root = event.target.closest(rootSelector);
    if (!root) return;
    instances.get(root)?.reset({ focus: false, announce: false });
    event.target.scrollIntoView({ block: 'nearest', behavior: 'auto' });
  });
})();
